import { createContext, ReactElement, useContext, useState } from 'react';
import { useSearchPage } from 'components/SearchPage/hooks/useSearchPage';
import type { ISearchPageDispatch } from 'components/SearchPage/hooks/useSearchPage';
import type {
  ISearchPageAds,
  ISearchPageBreadcrumbs,
  ISearchPagePaging,
} from 'api/types/searchPageApiTypes';
import { ViewType } from 'components/SearchPage/SearchPage';

export type SearchPageStateType = {
  baseUrl: string;
  userId?: string | string[];
  viewType: ViewType | undefined;
  keywordSearchTerm: string;
  section: string;
  sort: string;
  paging: ISearchPagePaging;
  saved: boolean;
  page: number;
  breadcrumbs: ISearchPageBreadcrumbs[];
  recommendations: ISearchPageAds[];
};
export type SearchPageDispatchType = ISearchPageDispatch & {
  setViewType: (viewType: ViewType) => void;
};

export const SearchPageDispatchContext = createContext(
  {} as SearchPageDispatchType,
);

export const SearchPageStateContext = createContext({} as SearchPageStateType);

export const useSearchPageDispatch = (): SearchPageDispatchType =>
  useContext(SearchPageDispatchContext);
export const useSearchPageState = (): SearchPageStateType =>
  useContext(SearchPageStateContext);

export const SearchPageProvider = ({
  baseUrl,
  userId,
  keywordSearchTerm,
  section,
  sort,
  paging,
  viewType,
  saved,
  initialBreadcrumbs,
  initialRecommendations = [],
  children,
}: {
  baseUrl: string;
  userId?: string | string[];
  keywordSearchTerm: string;
  section: string;
  sort: string;
  paging: ISearchPagePaging;
  viewType: ViewType;
  saved: boolean;
  initialBreadcrumbs: ISearchPageBreadcrumbs[];
  initialRecommendations?: ISearchPageAds[];
  children: ReactElement;
}): ReactElement => {
  const [viewTypeState, setViewType] = useState<ViewType | undefined>(viewType);
  const { state, dispatch } = useSearchPage(
    section,
    keywordSearchTerm,
    sort,
    paging,
    initialBreadcrumbs,
    initialRecommendations,
  );

  return (
    <SearchPageDispatchContext.Provider
      value={{
        setViewType,
        ...dispatch,
      }}
    >
      <SearchPageStateContext.Provider
        value={{
          baseUrl,
          userId,
          saved,
          viewType: viewTypeState,
          ...state,
        }}
      >
        {children}
      </SearchPageStateContext.Provider>
    </SearchPageDispatchContext.Provider>
  );
};
